import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

let _supabaseInstance: SupabaseClient | null = null;

const getSupabase = (): SupabaseClient => {
  if (!_supabaseInstance) {
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error(
        'Supabase credentials missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your project settings.'
      );
    }
    _supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }
  return _supabaseInstance;
};

// Use a Proxy to allow the existing 'import { supabase }' syntax to work 
// while lazily initializing and handling errors gracefully.
export const supabase = new Proxy({} as SupabaseClient, {
  get(_, prop) {
    const client = getSupabase();
    const value = (client as any)[prop];
    return typeof value === 'function' ? value.bind(client) : value;
  }
});

export const loginWithEmail = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
};

export const signUpWithEmail = async (email: string, password: string) => {
  let ref: string | null = null;
  let ageAt: string | null = null;
  try { ref = localStorage.getItem('yhuu_ref'); ageAt = localStorage.getItem('yhuu_age_confirmed_at'); } catch { /* ignore */ }
  const meta: Record<string, string> = {};
  if (ref) meta.ref = ref;
  if (ageAt) meta.age_confirmed_at = ageAt;
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: Object.keys(meta).length ? { data: meta } : undefined,
  });
  if (error) throw error;
  return data;
};

// Canonical production domain. Falls back to whatever origin the app is
// running on (e.g. localhost during dev) so local/preview testing still works,
// but on any deployed origin (including yhuu.vercel.app) it forces users
// back to the real domain instead of whichever host they happened to load from.
const PRODUCTION_ORIGIN = 'https://yhuu.life';

const getAuthRedirectOrigin = () => {
  if (typeof window === 'undefined') return PRODUCTION_ORIGIN;
  const { origin, hostname } = window.location;
  const isLocal = hostname === 'localhost' || hostname === '127.0.0.1';
  return isLocal ? origin : PRODUCTION_ORIGIN;
};

export const loginWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: getAuthRedirectOrigin(),
    },
  });
  if (error) throw error;
  return data;
};

export const logout = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};
